import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useApolloClient } from "@apollo/client";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Grid } from "@mui/material";
import { differenceInCalendarDays, endOfDay, startOfDay, subDays } from "date-fns";
import { selectProject } from "../../../features/project/projectSlice";
import { SettingContainer, SubTabs, SubTab } from "../../../commonStyles";
import { useAppDispatch } from "../../../app/hooks";
import { showErrorSnackbar } from "../../../features/snackbar/snackbarSlice";
import OverallSummary from "./userReport/OverallSummary";
import DailySummary from "./userReport/DailySummary";
import { GETPROJECTACTIVITY, IDayStats, toDays } from "./userReport/types";

const MAX_DAYS = 31;

const UserReporting = (props: ChildProps) => {
  const { rangeRef } = props;
  const project = useSelector(selectProject);
  const client = useApolloClient();
  const storeDispatch = useAppDispatch();

  const [tab, setTab] = useState<number>(0);
  const [dateStart, setDateStart] = useState<Date>(subDays(startOfDay(new Date()), 6));
  const [dateEnd, setDateEnd] = useState<Date>(startOfDay(new Date()));
  const [days, setDays] = useState<IDayStats[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const requestId = useRef(0);

  // Shared with Reporting's Download Project Report, without re-rendering the tabs
  useEffect(() => {
    if (rangeRef) rangeRef.current = { dateStart, dateEnd };
  }, [rangeRef, dateStart, dateEnd]);

  useEffect(() => {
    if (!project || !isValidDate(dateStart) || !isValidDate(dateEnd)) return;
    const id = ++requestId.current;

    const load = async () => {
      setLoading(true);
      try {
        const { data } = await client.query({
          query: GETPROJECTACTIVITY,
          fetchPolicy: "network-only",
          variables: { project: project._id, dateStart: startOfDay(dateStart), dateEnd: endOfDay(dateEnd) },
        });
        if (id !== requestId.current) return;
        setDays(toDays(data.projectActivityStats, dateStart, dateEnd));
      } catch (err) {
        if (id === requestId.current) {
          setDays([]);
          storeDispatch(showErrorSnackbar("Error getting user stats"));
        }
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStart, dateEnd, project?._id]);

  const handleRange = (start: Date | null, end: Date | null) => {
    if (!start || !end || !isValidDate(start) || !isValidDate(end)) return;
    if (start.valueOf() > end.valueOf()) {
      return storeDispatch(showErrorSnackbar("Start Date cannot be greater than End Date"));
    }
    if (differenceInCalendarDays(end, start) + 1 > MAX_DAYS) {
      return storeDispatch(showErrorSnackbar(`Date range cannot exceed ${MAX_DAYS} days`));
    }
    setDateStart(startOfDay(start));
    setDateEnd(startOfDay(end));
  };

  // Details defaults to today's calls; the range can still be changed after
  const handleTab = (value: number) => {
    if (value === 1 && tab !== 1) {
      setDateStart(startOfDay(new Date()));
      setDateEnd(startOfDay(new Date()));
    }
    setTab(value);
  };

  return (
    <SettingContainer>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label="Start Date" value={dateStart} onChange={(value: any) => handleRange(value, dateEnd)} sx={{ width: "100%" }} />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label="End Date" value={dateEnd} onChange={(value: any) => handleRange(dateStart, value)} sx={{ width: "100%" }} />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <SubTabs value={tab} onChange={(_, value) => handleTab(value)} sx={{ mt: 2 }}>
        <SubTab label="Summary" />
        <SubTab label="Details" />
      </SubTabs>

      {tab === 0 ? (
        <OverallSummary
          projectId={project?._id || ""}
          projectName={project?.name || ""}
          dateStart={dateStart}
          dateEnd={dateEnd}
          days={days}
          loading={loading}
        />
      ) : (
        <DailySummary projectId={project?._id || ""} dateStart={dateStart} dateEnd={dateEnd} />
      )}
    </SettingContainer>
  );
};

const isValidDate = (date: any) => date instanceof Date && !isNaN(date.valueOf());

export interface IReportRange {
  dateStart: Date;
  dateEnd: Date;
}

interface ChildProps {
  rangeRef?: MutableRefObject<IReportRange>;
}

export default UserReporting;
